import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Paper,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import ShareIcon from "@mui/icons-material/Share";
import EmailIcon from "@mui/icons-material/Email";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { toast } from "react-toastify";

import AppEmailModal from "../common/AppEmailModal";
import AppButton from "../buttons/AppButton";
import AppCircularProgress from "../common/AppCircularProgress";

import {
  getQuotationPdf,
  emailQuotation,
} from "../../redux/actions/quotationActions";
import { quotationActions } from "../../redux/slices/quotation";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { QuotationResponseDto } from "../../shared/dtos/quotation/QuotationResponseDto";

interface QuotationPdfTabProps {
  quote: QuotationResponseDto;
}

const QuotationPdfTab: React.FC<QuotationPdfTabProps> = ({ quote }) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    pdfUrl,
    loadingPdf,
    errorPdf: errorLoadingPdf,
    loadingEmail,
  } = useAppSelector((state) => state.quotation);

  const [emailModalOpen, setEmailModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (quote?.id && !pdfUrl && !loadingPdf)
      dispatch(getQuotationPdf(quote.id));
  }, [dispatch, quote?.id, pdfUrl, loadingPdf]);

  useEffect(() => {
    return () => {
      dispatch(quotationActions.clearPdfUrl());
    };
  }, [dispatch]);

  const handleDownload = (): void => {
    if (!pdfUrl) return;
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `Quote_${quote.quoteNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = (): void => {
    if (!pdfUrl) return;
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = pdfUrl;
    document.body.appendChild(iframe);
    iframe.onload = () =>
      setTimeout(() => {
        if (iframe.contentWindow) {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        }
        setTimeout(() => document.body.removeChild(iframe), 2000);
      }, 100);
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      toast.success("Link copied to clipboard!");
    });
    // handleMenuClose();
  };

  const handleEmailClick = (): void => {
    setEmailModalOpen(true);
  };

  const handleEmailSubmit = async (email: string): Promise<void> => {
    const success = await dispatch(emailQuotation(quote.id, { email: email }));
    if (success) {
      setEmailModalOpen(false);
    }
  };

  // --- RENDER FUNCTIONS FOR ACTIONS ---
  const renderDesktopActions = (): React.JSX.Element => (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{ mb: 3, justifyContent: "flex-end" }}
    >
      <AppButton
        variant="outlined"
        color="primary"
        startIcon={<ShareIcon />}
        onClick={handleShare}
      >
        Share Link
      </AppButton>
      <AppButton
        variant="outlined"
        color="secondary"
        startIcon={<EmailIcon />}
        onClick={handleEmailClick}
      >
        Email
      </AppButton>
      <AppButton
        variant="contained"
        color="warning"
        startIcon={<FileDownloadIcon />}
        onClick={handleDownload}
        disabled={!pdfUrl || loadingPdf}
      >
        Download
      </AppButton>
      <AppButton
        variant="contained"
        color="primary"
        startIcon={<PrintIcon />}
        onClick={handlePrint}
        disabled={!pdfUrl || loadingPdf}
      >
        Print Quote
      </AppButton>
    </Stack>
  );

  const renderMobileActions = (): React.JSX.Element => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 2,
        px: 1,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: "bold", color: "text.primary" }}
      >
        Document Actions
      </Typography>
      <Stack direction="row" spacing={0.5}>
        <IconButton
          color="primary"
          onClick={handlePrint}
          disabled={!pdfUrl || loadingPdf}
        >
          <PrintIcon />
        </IconButton>
        <IconButton color="primary" onClick={handleShare}>
          <ShareIcon />
        </IconButton>
        <IconButton
          color="primary"
          onClick={handleDownload}
          disabled={!pdfUrl || loadingPdf}
        >
          <FileDownloadIcon />
        </IconButton>
        <IconButton color="primary" onClick={handleEmailClick}>
          <EmailIcon />
        </IconButton>
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {isMobile ? renderMobileActions() : renderDesktopActions()}

      <Paper
        elevation={0}
        sx={{
          flex: 1,
          minHeight: "650px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: (t) => (t.palette.mode === "dark" ? "#1b1b1b" : "#e0e0e0"),
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {loadingPdf ? (
          <AppCircularProgress />
        ) : errorLoadingPdf ? (
          <>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
            <Typography sx={{ color: "error.main" }}>
              {errorLoadingPdf}
            </Typography>
            <AppButton
              pill
              variant="outlined"
              color={"error"}
              sx={{ mt: 2 }}
              onClick={() => dispatch(getQuotationPdf(quote.id))}
            >
              Retry
            </AppButton>
          </>
        ) : pdfUrl ? (
          <iframe
            src={pdfUrl}
            title="PDF"
            width="100%"
            height="100%"
            style={{ border: "none" }}
          />
        ) : null}
      </Paper>

      <AppEmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        onSubmit={handleEmailSubmit}
        loading={loadingEmail}
        title="Email Document"
        description={`Send a copy of document #${quote.quoteNumber} directly to an email.`}
      />
    </Box>
  );
};
export default QuotationPdfTab;
